import type { CRSData, FragmentsModel } from '@thatopen/fragments'
import * as THREE from 'three'
import { getAttributes, type KeyValue } from './ifcItemData'
import { fragmentsReturnsWorld } from './geoTransform'

export interface CategoryStats {
  total: number
  perCategory: Array<{ category: string; count: number; isType: boolean }>
  typeCount: number
  instanceCount: number
}

export interface ProjectInfo {
  localId: number
  rows: KeyValue[]
}

export interface ModelInfo {
  crs: CRSData | null
  coordinates: number[] | null
  stats: CategoryStats
  /** IfcProject attributes (Name, Description, LongName, Phase, ...);
   *  null when the model has no IfcProject or the read failed. */
  project: ProjectInfo | null
}

/** Fetch the IfcProject entity's scalar attributes via Fragments (no
 *  parallel parser needed). Models legally have exactly one IfcProject;
 *  we take the first. */
async function loadProjectInfo(
  model: FragmentsModel,
): Promise<ProjectInfo | null> {
  try {
    const bucket = await model.getItemsOfCategories([/^IFCPROJECT$/])
    const ids = Object.values(bucket).flatMap((arr) => arr ?? [])
    if (ids.length === 0) return null
    const data = await model.getItemsData([ids[0]], {
      attributesDefault: true,
      relationsDefault: { attributes: false, relations: false },
    })
    const item = data?.[0] as Record<string, unknown> | undefined
    if (!item) return null
    return { localId: ids[0], rows: getAttributes(item) }
  } catch {
    return null
  }
}

// One-shot loader that pulls CRS, world coordinates, category counts
// and the IfcProject attributes from the model in parallel. Type
// categories (IFC*TYPE) are counted separately so the UI can flag
// whether the model uses IFC types or is instance-only.
export async function loadModelInfo(model: FragmentsModel): Promise<ModelInfo> {
  const [crs, coords, byCategory, project] = await Promise.all([
    model.getCRS().catch(() => null),
    model.getCoordinates().catch(() => null),
    model.getItemsOfCategories([/.*/]).catch(() => ({})),
    loadProjectInfo(model),
  ])

  const perCategory = Object.entries(byCategory)
    .map(([category, ids]) => {
      const count = Array.isArray(ids) ? ids.length : 0
      const isType = /TYPE$/i.test(category)
      return { category, count, isType }
    })
    .sort((a, b) => b.count - a.count)

  const typeCount = perCategory
    .filter((c) => c.isType)
    .reduce((s, c) => s + c.count, 0)
  const instanceCount = perCategory
    .filter((c) => !c.isType)
    .reduce((s, c) => s + c.count, 0)

  return {
    crs,
    coordinates: coords,
    stats: {
      total: typeCount + instanceCount,
      perCategory,
      typeCount,
      instanceCount,
    },
    project,
  }
}

export interface CoordinateInfo {
  worldPosition: THREE.Vector3 | null
  bbox: THREE.Box3 | null
  ancestorPositions: Array<{
    localId: number
    worldPosition: THREE.Vector3 | null
    extentBbox: THREE.Box3 | null
  }>
}

// Pulls world position + bbox for the element, plus an approximated
// "position" for each ancestor container. Fragments does not preserve
// IfcLocalPlacement, so the container itself returns no position from
// getPositions(). We instead compute the bbox union of all the
// elements descending from each container and use the centroid of
// that union as the container's effective position. The result is
// not the IfcLocalPlacement origin (which would require web-ifc
// re-parsing) but it's a meaningful spatial centre and lets us show
// useful element-to-container deltas.
export async function loadCoordinateInfo(
  model: FragmentsModel,
  localId: number,
  ancestorIds: number[],
  getDescendants: (localId: number) => number[],
): Promise<CoordinateInfo> {
  const [elementPositions, elementBoxes] = await Promise.all([
    model.getPositions([localId]).catch(() => [] as THREE.Vector3[]),
    model.getBoxes([localId]).catch(() => [] as THREE.Box3[]),
  ])
  // A versão atual do Fragments devolve positions/boxes per-item JÁ em
  // coordenadas de MUNDO (fragmentsReturnsWorld); versões anteriores
  // devolviam no frame local. Aplicar matrixWorld cegamente duplicava
  // o transform geo e o painel mostrava coordenadas a 2× da posição
  // real em modelos georreferenciados.
  const alreadyWorld = fragmentsReturnsWorld(model)
  model.object.updateMatrixWorld(true)
  const mw = alreadyWorld
    ? new THREE.Matrix4()
    : model.object.matrixWorld
  const elementPositionsWorld = elementPositions.map((p) =>
    p ? p.clone().applyMatrix4(mw) : p,
  )
  const elementBoxesWorld = elementBoxes.map((b) =>
    b && !b.isEmpty() ? b.clone().applyMatrix4(mw) : b,
  )

  const ancestorPositions: CoordinateInfo['ancestorPositions'] = []
  for (const aId of ancestorIds) {
    const descendants = getDescendants(aId)
    if (descendants.length === 0) {
      ancestorPositions.push({
        localId: aId,
        worldPosition: null,
        extentBbox: null,
      })
      continue
    }
    const SAMPLE_CAP = 4000
    const ids =
      descendants.length > SAMPLE_CAP
        ? sampleEvenly(descendants, SAMPLE_CAP)
        : descendants
    const boxes = await model.getBoxes(ids).catch(() => [] as THREE.Box3[])
    const union = new THREE.Box3()
    for (const b of boxes) {
      if (b && !b.isEmpty()) union.union(b.clone().applyMatrix4(mw))
    }
    if (union.isEmpty()) {
      ancestorPositions.push({
        localId: aId,
        worldPosition: null,
        extentBbox: null,
      })
    } else {
      const center = new THREE.Vector3()
      union.getCenter(center)
      ancestorPositions.push({
        localId: aId,
        worldPosition: center,
        extentBbox: union,
      })
    }
  }

  return {
    worldPosition: elementPositionsWorld[0] ?? null,
    bbox: elementBoxesWorld[0] ?? null,
    ancestorPositions,
  }
}

function sampleEvenly<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) return arr
  const out: T[] = []
  const step = arr.length / n
  for (let i = 0; i < n; i++) out.push(arr[Math.floor(i * step)])
  return out
}

export function formatCoord(n: number, digits = 3): string {
  if (!Number.isFinite(n)) return '–'
  return n.toFixed(digits)
}
